import * as React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import AnalyticsRoundedIcon from '@mui/icons-material/AnalyticsRounded';
import AssignmentRoundedIcon from '@mui/icons-material/AssignmentRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import HelpRoundedIcon from '@mui/icons-material/HelpRounded';
import GrassRoundedIcon from '@mui/icons-material/GrassRounded';
import HistoryRoundedIcon from '@mui/icons-material/HistoryRounded';
import GroupsRoundedIcon from '@mui/icons-material/GroupsRounded';
import ForumRoundedIcon from '@mui/icons-material/ForumRounded';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useGrow, GROW_STAGES } from '../context/GrowContext';

const menuItems = [
  { text: 'Grow Log', icon: <HistoryRoundedIcon />, path: '/grow-log' },
  { text: 'The Collective', icon: <GroupsRoundedIcon />, path: '/collective' },
  { text: 'Forum', icon: <ForumRoundedIcon />, path: '/forum' },
  { text: 'Presets', icon: <TuneRoundedIcon />, path: '/presets' },
  { text: 'Analytics', icon: <AnalyticsRoundedIcon />, path: '/analytics' },
  { text: 'Tasks', icon: <AssignmentRoundedIcon />, path: '/tasks' },
];

const secondaryListItems = [
  { text: 'About', icon: <InfoRoundedIcon />, path: '/about' },
  { text: 'Feedback', icon: <HelpRoundedIcon />, path: '/feedback' },
];

export default function MenuContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const { grows, setActiveGrowId } = useGrow();
  const [growsOpen, setGrowsOpen] = React.useState(true);

  // Detect if we're in demo or app mode based on URL
  const isDemo = location.pathname.startsWith('/demo');
  const basePath = isDemo ? '/demo' : '/app';

  const handleNavigation = (path) => {
    navigate(basePath + path);
  };

  const handleGrowNavigation = (growId) => {
    setActiveGrowId(growId);
    navigate(`${basePath}/grow/${growId}`);
  };

  const handleDashboardNavigation = () => {
    navigate(basePath);
  };

  const isGrowPage = location.pathname.includes('/grow/');
  const currentGrowId = isGrowPage ? location.pathname.split('/grow/')[1] : null;

  return (
    <Stack sx={{ flexGrow: 1, p: 1, justifyContent: 'space-between' }}>
      <List dense>
        {/* Demo mode indicator */}
        {isDemo && (
          <ListItem sx={{ mb: 1 }}>
            <Chip
              label="Demo Mode"
              size="small"
              color="warning"
              sx={{
                width: '100%',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
              }}
            />
          </ListItem>
        )}

        {/* Dashboard with expandable grows */}
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            selected={location.pathname === basePath || location.pathname === basePath + '/'}
            onClick={handleDashboardNavigation}
          >
            <ListItemIcon>
              <Box
                component="img"
                src="/icon.svg"
                alt=""
                sx={{ width: 28, height: 28 }}
              />
            </ListItemIcon>
            <ListItemText primary="Dashboard" />
            {grows.filter(g => g.status === 'active').length > 0 && (
              <Box
                onClick={(e) => {
                  e.stopPropagation();
                  setGrowsOpen(!growsOpen);
                }}
                sx={{ display: 'flex', alignItems: 'center' }}
              >
                {growsOpen ? <ExpandLess /> : <ExpandMore />}
              </Box>
            )}
          </ListItemButton>
        </ListItem>

        {/* Indented grows list - only show active grows */}
        {grows.filter(g => g.status === 'active').length > 0 && (
          <Collapse in={growsOpen} timeout="auto" unmountOnExit>
            <List component="div" disablePadding dense>
              {grows.filter(g => g.status === 'active').map((grow) => (
                <ListItem key={grow.id} disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    selected={currentGrowId === grow.id}
                    onClick={() => handleGrowNavigation(grow.id)}
                    sx={{ pl: 4 }}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <GrassRoundedIcon sx={{ fontSize: 18 }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={grow.name}
                      secondary={GROW_STAGES[grow.currentStage]?.label}
                      primaryTypographyProps={{
                        fontSize: '0.85rem',
                        noWrap: true,
                      }}
                      secondaryTypographyProps={{
                        fontSize: '0.7rem',
                        noWrap: true,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}

        {/* Other menu items */}
        {menuItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              selected={location.pathname === basePath + item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <List dense>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding sx={{ display: 'block' }}>
            <ListItemButton
              selected={location.pathname === basePath + item.path}
              onClick={() => handleNavigation(item.path)}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Stack>
  );
}
